import React, { useEffect, useState } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { Box, Button, Modal, Typography } from '@mui/material';

const FLAG_KEY = 'sharpstack_projections_tutorial_v1';
const MOBILE_BREAKPOINT = 900;


// ───────────────────────────────────────────────────────────────────────────
// STEPS
// ───────────────────────────────────────────────────────────────────────────

const STEPS = [
  {
    target: '[data-tutorial="league-filter"]',
    title: 'League',
    content:
      'Select which sport to view projections for. You can also search by player name or matchup using the search bar to the right.',
    placement: 'bottom-start',
    skipBeacon: true,
  },
  {
    target: '.filter-section .MuiTabs-flexContainer',
    title: 'Position Tabs',
    content:
      'Quickly narrow projections to a specific position. The tab options adapt to the selected league — e.g. G/F/C for NBA, QB/RB/WR/TE for NFL.',
    placement: 'bottom-start',
    skipBeacon: true,
  },
  {
    target: '.data-grid-container',
    title: 'Projections Grid',
    content:
      'Each row is a player with projected stats derived from current sportsbook market odds. Columns adapt to the league (Points/Rebounds/Assists for NBA, passing/rushing/receiving for NFL). Click any column header to sort.',
    placement: 'center',
    skipBeacon: true,
  },
  {
    target: '[data-tutorial="projections-actions"]',
    title: 'Copy & Export',
    content:
      'Copy projections to your clipboard or export them to CSV for further analysis in your own spreadsheets or models.',
    placement: 'bottom-end',
    skipBeacon: true,
  },
];

// ───────────────────────────────────────────────────────────────────────────
// CUSTOM TOOLTIP
// ───────────────────────────────────────────────────────────────────────────

const CustomTooltip = ({
  index,
  size,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
}) => (
  <div
    {...tooltipProps}
    style={{
      backgroundColor: '#151a28',
      border: '1px solid #262d40',
      borderRadius: 12,
      padding: '20px 22px 16px',
      width: 340,
      boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      fontFamily: 'inherit',
    }}
  >
    <div
      style={{
        display: 'inline-block',
        background: '#4caf50',
        color: '#000',
        fontSize: 10,
        fontWeight: 700,
        padding: '3px 10px',
        borderRadius: 100,
        marginBottom: 10,
        letterSpacing: 0.5,
      }}
    >
      STEP {index + 1} OF {size}
    </div>

    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: '0 0 6px 0' }}>
      {step.title}
    </h3>

    <p style={{ fontSize: 13, color: '#8892a8', lineHeight: 1.55, margin: '0 0 16px 0' }}>
      {step.content}
    </p>

    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <button
        {...skipProps}
        style={{
          background: 'none',
          border: 'none',
          color: '#666',
          fontSize: 11,
          cursor: 'pointer',
          padding: 0,
          fontFamily: 'inherit',
        }}
      >
        Skip tour
      </button>

      <div style={{ display: 'flex', gap: 6 }}>
        {index > 0 && (
          <button
            {...backProps}
            style={{
              background: 'none',
              border: '1px solid #262d40',
              color: '#8892a8',
              padding: '7px 12px',
              borderRadius: 6,
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Back
          </button>
        )}
        <button
          {...primaryProps}
          style={{
            background: '#4caf50',
            color: '#000',
            border: 'none',
            padding: '7px 16px',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {isLastStep ? 'Got it ✓' : 'Next →'}
        </button>
      </div>
    </div>
  </div>
);

// ───────────────────────────────────────────────────────────────────────────
// JOYRIDE GLOBAL STYLES
// ───────────────────────────────────────────────────────────────────────────

const joyrideStyles = {
  options: {
    zIndex: 10000,
    overlayColor: 'rgba(0, 0, 0, 0.72)',
    arrowColor: '#151a28',
  },
};

// ───────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ───────────────────────────────────────────────────────────────────────────

export default function ProjectionsTutorial() {
  const [showWelcome, setShowWelcome] = useState(false);
  const [runTour, setRunTour] = useState(false);

  useEffect(() => {
    if (window.innerWidth < MOBILE_BREAKPOINT) return;
    if (localStorage.getItem(FLAG_KEY)) return;

    const t = setTimeout(() => setShowWelcome(true), 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handleReplay = (e) => {
      if (e.detail?.flag !== FLAG_KEY) return;
      setRunTour(false);
      setShowWelcome(true);
    };
    window.addEventListener('sharpstack:replay-tutorial', handleReplay);
    return () => window.removeEventListener('sharpstack:replay-tutorial', handleReplay);
  }, []);

  const startTour = () => {
    setShowWelcome(false);
    setTimeout(() => setRunTour(true), 200);
  };

  const skipTour = () => {
    setShowWelcome(false);
    localStorage.setItem(FLAG_KEY, 'true');
  };

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      localStorage.setItem(FLAG_KEY, 'true');
      setRunTour(false);
    }
  };

  return (
    <>
      {/* INTRO MODAL */}
      <Modal open={showWelcome} onClose={() => {}} disableEscapeKeyDown>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#141820',
            border: '1px solid #252a38',
            borderRadius: '16px',
            p: '44px 40px 36px',
            width: 440,
            maxWidth: '90vw',
            textAlign: 'center',
            outline: 'none',
            boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #4caf50, #2e7d32)',
              margin: '0 auto 22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 26,
              color: '#000',
              fontWeight: 800,
            }}
          >
            S
          </Box>

          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#fff', mb: 1 }}>
            Projections
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: '#94a3b8',
              lineHeight: 1.6,
              mb: 4,
              px: 1,
            }}
          >
            Player projections derived from sportsbook market odds, filterable by league and position. Let's show you how.
          </Typography>

          <Button
            onClick={startTour}
            sx={{
              backgroundColor: '#4caf50',
              color: '#000',
              fontWeight: 700,
              px: 4,
              py: 1.25,
              fontSize: 14,
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': { backgroundColor: '#66bb6a' },
            }}
          >
            Show Me
          </Button>

          <Box sx={{ mt: 1.5 }}>
            <Button
              onClick={skipTour}
              sx={{
                color: '#4a5068',
                fontSize: 13,
                textTransform: 'none',
                '&:hover': { color: '#94a3b8', background: 'none' },
              }}
            >
              I've got it
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* JOYRIDE TOUR */}
      <Joyride
        steps={STEPS}
        run={runTour}
        onEvent={handleJoyrideCallback}
        continuous
        disableOverlayClose
        tooltipComponent={CustomTooltip}
        styles={joyrideStyles}
      />
    </>
  );
}